/*
  Sunset Editorial — theme.js
  - Bandeau annonce : fermeture mémorisée 7 jours par cookie
  - Menus <details> : fermeture au clic extérieur et à Échap
  - Tiroir panier AJAX (Section Rendering API, barre livraison offerte rendue en Liquid)
  - Fiche produit : pastilles de taille, prix en direct, ?variant=, ajout AJAX, CTA sticky mobile
  - Galerie mobile, filtres de collection, recommandations produits, quantité et partage
*/
(() => {
  'use strict';

  const theme = window.theme || {};
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const CART_SECTION = 'cart-drawer';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-bubble]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.hidden = count === 0;
    });
  };

  /* ---------- Bandeau annonce ---------- */

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-announcement-close]')) return;
    document.cookie = 'se_announcement=hidden; max-age=604800; path=/; SameSite=Lax';
    document.documentElement.classList.add('announcement-hidden');
  });

  /* ---------- Menus <details> (recherche, menu mobile, sous-menus) ---------- */

  const closeDetails = (details, restoreFocus) => {
    if (!details) return;
    details.removeAttribute('open');
    if (restoreFocus) details.querySelector('summary')?.focus();
  };

  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-autoclose][open]').forEach((details) => {
      if (!details.contains(event.target)) closeDetails(details);
    });
    const closer = event.target.closest('[data-details-close]');
    if (closer) closeDetails(closer.closest('details'), true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-autoclose][open]').forEach((details) => closeDetails(details, true));
    document.querySelector('cart-drawer.is-open')?.close();
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details.open && details.matches?.('[data-search]')) {
        details.querySelector('input[type="search"]')?.focus();
      }
    },
    true
  );

  /* ---------- Tiroir panier ---------- */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.trapFocus(event));

      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-cart-toggle]');
        if (!toggle || document.body.classList.contains('template-cart')) return;
        event.preventDefault();
        this.open(toggle);
      });

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh();
      });
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      document.documentElement.classList.add('drawer-open');
      requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('drawer-open');
      this.trigger?.focus?.({ preventScroll: true });
    }

    onClick(event) {
      if (event.target.closest('[data-drawer-close]')) {
        this.close();
        return;
      }
      const lineButton = event.target.closest('[data-line]');
      if (lineButton) {
        this.changeLine(Number(lineButton.dataset.line), Number(lineButton.dataset.quantity));
      }
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.classList.contains('is-open')) return;
      const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async changeLine(line, quantity) {
      this.classList.add('is-busy');
      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line,
            quantity: Math.max(0, quantity),
            sections: [CART_SECTION],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
        if (!this.render(data.sections)) await this.refresh();
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.classList.remove('is-busy');
      }
    }

    /* Remplace le contenu du tiroir par la version rendue côté serveur. */
    render(sections) {
      const html = sections && sections[CART_SECTION];
      if (!html) return false;
      const fresh = parseHTML(html).getElementById('CartDrawer-inner');
      const current = this.querySelector('#CartDrawer-inner');
      if (!fresh || !current) return false;
      current.replaceWith(document.importNode(fresh, true));
      updateCartCount(Number(fresh.dataset.itemCount || 0));
      return true;
    }

    async refresh() {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${CART_SECTION}`);
        this.render(await response.json());
      } catch (error) {
        /* le panier sera à jour au prochain chargement de page */
      }
    }

    showError(message) {
      const box = this.querySelector('[data-cart-error]');
      if (!box) return;
      box.textContent = message || strings.error;
      box.hidden = false;
    }
  }

  define('cart-drawer', CartDrawer);

  /* ---------- Fiche produit ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.querySelector('form[data-product-form]');
      this.idInput = this.form?.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add-button]');
      this.stickyBar = this.querySelector('[data-sticky-atc]');
      this.stickyButton = this.querySelector('[data-sticky-button]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.onOptionChange();
      });
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
      this.initSticky();
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-group]'), (group) => {
        return group.querySelector('[data-option-input]:checked')?.value;
      });
    }

    findVariant(options) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-group]').forEach((group, index) => {
        const label = group.querySelector('[data-selected-label]');
        const input = group.querySelector('[data-option-input]:checked');
        if (label) label.textContent = input?.dataset.label || selected[index] || '';
      });
      this.updateAvailability();
      this.hideError();

      const variant = this.findVariant(selected);
      if (!variant) {
        this.setButtons(false, strings.unavailable);
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      this.renderPrice(variant);
      this.setButtons(variant.available, strings.soldOut);
      this.querySelectorAll('[data-sticky-variant]').forEach((el) => {
        el.textContent = variant.label || variant.title;
      });

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());

      if (variant.media) this.querySelector('media-gallery')?.goToMedia(variant.media);
    }

    renderPrice(variant) {
      this.querySelectorAll('[data-price-current]').forEach((el) => {
        el.innerHTML = variant.price;
      });
      this.querySelectorAll('[data-price-compare]').forEach((el) => {
        el.innerHTML = variant.compare || '';
        el.hidden = !variant.compare;
      });
      this.querySelectorAll('[data-price]').forEach((el) => {
        el.classList.toggle('price--on-sale', Boolean(variant.compare));
      });
    }

    setButtons(available, message) {
      if (this.addButton) {
        this.addButton.disabled = !available;
        const label = this.addButton.querySelector('[data-add-label]');
        if (label) label.textContent = available ? strings.addToCart : message;
      }
      if (this.stickyButton) {
        this.stickyButton.disabled = !available;
        this.stickyButton.textContent = available ? strings.addShort : message;
      }
    }

    /* Barre les pastilles dont la combinaison n'existe pas ou est épuisée. */
    updateAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-group]').forEach((group, index) => {
        group.querySelectorAll('[data-option-input]').forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const variant = this.findVariant(candidate);
          const label = this.querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle('is-unavailable', !variant || !variant.available);
        });
      });
    }

    async onSubmit(event) {
      const drawer = document.querySelector('cart-drawer');
      if (theme.cartType !== 'drawer' || !drawer) return;
      event.preventDefault();
      if (!this.addButton || this.addButton.disabled || this.addButton.classList.contains('is-loading')) return;

      this.hideError();
      this.setLoading(true);
      const formData = new FormData(this.form);
      formData.append('sections', CART_SECTION);
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
        if (!drawer.render(data.sections)) await drawer.refresh();
        drawer.open(event.submitter || this.addButton);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      [this.addButton, this.stickyButton].forEach((button) => {
        if (!button) return;
        button.classList.toggle('is-loading', loading);
        button.setAttribute('aria-busy', String(loading));
      });
      const label = this.addButton?.querySelector('[data-add-label]');
      if (label) label.textContent = loading ? strings.adding : strings.addToCart;
    }

    showError(message) {
      const box = this.querySelector('[data-form-error]');
      if (!box) return;
      box.textContent = message || strings.error;
      box.hidden = false;
      if (this.stickyBar?.classList.contains('is-visible')) {
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    hideError() {
      const box = this.querySelector('[data-form-error]');
      if (box) box.hidden = true;
    }

    /* CTA sticky : visible dès que le bouton d'origine n'est plus entièrement à l'écran (au-dessus ou en dessous). */
    initSticky() {
      if (!this.stickyBar || !this.addButton || !('IntersectionObserver' in window)) return;
      const header = document.querySelector('.header--sticky');
      const offset = header ? Math.round(header.getBoundingClientRect().height) : 0;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const visible = entry.intersectionRatio < 1;
          this.stickyBar.classList.toggle('is-visible', visible);
          this.stickyBar.setAttribute('aria-hidden', String(!visible));
          if (this.stickyButton) this.stickyButton.tabIndex = visible ? 0 : -1;
        },
        { threshold: [0, 1], rootMargin: `-${offset}px 0px 0px 0px` }
      );
      observer.observe(this.addButton);
    }
  }

  define('product-info', ProductInfo);

  /* ---------- Galerie produit (slider mobile, pile d'images en desktop) ---------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      if (!this.track || this.dots.length === 0) return;

      let frame;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.syncDots());
        },
        { passive: true }
      );
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goToIndex(Number(dot.dataset.galleryDot)));
      });
    }

    isSlider() {
      return this.track && window.getComputedStyle(this.track).display === 'flex';
    }

    goToIndex(index) {
      const slide = this.slides[index];
      if (!slide || !this.isSlider()) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goToIndex(index);
    }

    syncDots() {
      const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
  }

  define('media-gallery', MediaGallery);

  /* ---------- Filtres de collection : soumission automatique ---------- */

  class FacetForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.addEventListener('change', (event) => {
        if (this.form && event.target.matches('input[type="checkbox"], select')) this.submit();
      });
    }

    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      Array.from(params.keys()).forEach((key) => {
        if (params.getAll(key).every((value) => value === '')) params.delete(key);
      });
      this.classList.add('is-loading');
      window.location.search = params.toString();
    }
  }

  define('facet-form', FacetForm);

  /* ---------- Recommandations produit (chargées à l'approche) ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.ready || !this.dataset.url) return;
      this.ready = true;
      if (this.querySelector('.product-grid')) return;

      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          })
          .catch(() => {});
      };

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }

  define('product-recommendations', ProductRecommendations);

  /* ---------- Quantité (bloc de la fiche produit) ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-step]');
        if (!button || !this.input) return;
        const min = Number(this.input.min || 1);
        this.input.value = Math.max(min, (Number(this.input.value) || min) + Number(button.dataset.step));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  define('quantity-input', QuantityInput);

  /* ---------- Partage (bloc de la fiche produit) ---------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-url]');
    if (!button) return;
    const url = button.dataset.shareUrl;
    if (navigator.share) {
      try {
        await navigator.share({ title: button.dataset.shareTitle, url });
      } catch (error) {
        /* partage annulé par le visiteur */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      const status = button.closest('.product__share')?.querySelector('[data-share-status]');
      if (status) {
        status.textContent = strings.linkCopied;
        status.hidden = false;
      }
    } catch (error) {
      window.prompt('', url);
    }
  });

  /* ---------- Protection des visuels (Paramètres du thème > Protection des visuels) ---------- */

  // Pas de clic droit « Enregistrer l'image » ni de glisser-déposer sur les visuels. Dissuasif : une
  // capture d'écran reste possible ; la vraie protection est la résolution limitée des fichiers servis.
  if (document.body.classList.contains('protect-media')) {
    const protectedMedia = 'img, picture, .lightbox__stage, .hero__map, .poster-3d__scene, .poster-frame';
    document.addEventListener('contextmenu', (event) => {
      if (event.target.closest?.(protectedMedia)) event.preventDefault();
    });
    document.addEventListener('dragstart', (event) => {
      if (event.target.closest?.('img')) event.preventDefault();
    });
  }
})();
